import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiArrowDown, FiArrowUp, FiEye, FiInbox } from "react-icons/fi";

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function moneyLabel(value, currency) {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

export default function ClientTransactionTable({
  transactions,
  loading,
  currency,
  page,
  pageSize,
  sortDirection,
  onSortDate,
  onPageChange,
  onView,
}) {
  const pageCount = Math.max(1, Math.ceil(transactions.length / pageSize));
  const visibleRows = transactions.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-violet-100 border-t-violet-600" />
        <p className="mt-4 text-sm font-medium text-slate-600">
          Loading transactions…
        </p>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] border-collapse text-left">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-5 py-4">
                <button
                  type="button"
                  onClick={onSortDate}
                  className="inline-flex items-center gap-1.5 hover:text-slate-900"
                >
                  Date
                  <SafeIcon
                    icon={sortDirection === "asc" ? FiArrowUp : FiArrowDown}
                  />
                </button>
              </th>
              <th className="px-5 py-4">Description</th>
              <th className="px-5 py-4">Reference</th>
              <th className="px-5 py-4">Account</th>
              <th className="px-5 py-4 text-right">Debit</th>
              <th className="px-5 py-4 text-right">Credit</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {visibleRows.map((transaction) => (
              <tr key={transaction.id} className="text-sm hover:bg-slate-50/70">
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                  {dateLabel(transaction.date)}
                </td>
                <td className="max-w-[260px] px-5 py-4">
                  <p className="truncate font-semibold text-slate-800">
                    {transaction.description || "—"}
                  </p>
                </td>
                <td className="px-5 py-4 font-mono text-xs text-slate-500">
                  {transaction.batch_id
                    ? transaction.batch_id.slice(0, 8)
                    : "—"}
                </td>
                <td className="px-5 py-4">
                  <p className="font-medium text-slate-700">
                    {transaction.account?.name || "Account unavailable"}
                  </p>
                  {transaction.account?.code && (
                    <p className="mt-1 text-xs text-slate-400">
                      {transaction.account.code}
                    </p>
                  )}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-700">
                  {moneyLabel(transaction.debit, currency)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums text-slate-700">
                  {moneyLabel(transaction.credit, currency)}
                </td>
                <td className="px-5 py-4">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600">
                    {transaction.status || "Not provided"}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => onView(transaction)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold text-violet-700 transition hover:bg-violet-50"
                    aria-label="View transaction details"
                  >
                    <SafeIcon icon={FiEye} />
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleRows.length === 0 && (
        <div className="px-6 py-14 text-center">
          <SafeIcon icon={FiInbox} className="mx-auto text-3xl text-slate-300" />
          <h3 className="mt-3 text-sm font-bold text-slate-800">
            No transactions found
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Try changing your search or filters.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          {transactions.length
            ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(
                page * pageSize,
                transactions.length,
              )} of ${transactions.length}`
            : "0 transactions"}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="px-2 text-xs font-medium text-slate-500">
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}